/*
JavaScript component representing a drawer, managing its state and behavior.

Key features include:
- Dynamic Drawer Configuration: Allows customization of drawer side, title, content padding, and backdrop presence.
- State Management: Tracks the open and closing state of the drawer, and whether it's the first drawer opened.
- Interaction Handling: Responds to changes in the drawer's open attribute, resizing events, and close actions.
- Integration with Alpine.js: Utilizes Alpine.js for data binding and lifecycle management.

The component observes changes in the drawer's open attribute and adjusts its state accordingly. It handles opening, closing, and resizing of the drawer, ensuring smooth user experience.
*/

const drawer = (passedSide, passedTitle, contentPadding, forceBackdrop) => ({
  open: false,
  isClosing: false,
  isFirstDrawer: undefined,
  isScrollable: false,

  get sideNormalized() {
    // TODO: when nothing is passed consider auto switching based on screen size
    return this.drawerSide || passedSide || 'right';
  },

  get titleNormalized() {
    return this.drawerTitle ?? passedTitle;
  },

  get hasBackdrop() {
    return this.isFirstDrawer || forceBackdrop;
  },

  init() {
    this.mutationObserver = new MutationObserver(this.handleMutation.bind(this));
    this.mutationObserver.observe(this.$el, {
      attributes: true,
      attributeFilter: ['open'],
    });

    if (contentPadding) {
      this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
      this.resizeObserver.observe(this.$refs.middle);
    }
  },

  destroy() {
    this.mutationObserver.disconnect();
    this.resizeObserver?.disconnect();

    openDrawers.delete(this);
    if (openDrawers.size === 0) {
      enableBodyScroll();
    }
  },

  close() {
    this.$root.closest('dialog').close();
    this.$store.nav.isMobileNavOpen = false;
  },

  handleMutation() {
    if (this.isClosing) return;

    const { open: newOpen } = this.$el;

    this.open = newOpen;

    if (newOpen) {
      this.isFirstDrawer = openDrawers.size === 0;
      openDrawers.add(this);
      disableBodyScroll();
    } else {
      openDrawers.delete(this);
      if (openDrawers.size === 0) {
        enableBodyScroll();
      }
    }

    if (!newOpen) {
      this.isClosing = true;

      this.$el.showModal();

      // TODO: use transitionend event instead?
      setTimeout(() => {
        this.$dispatch('drawer-close-complete', { drawer: this });

        this.$el.close();

        Promise.resolve().then(() => {
          this.isClosing = false;
        });
      }, 300);
    }

    this.$dispatch(this.open ? 'drawer-open' : 'drawer-close', {
      drawer: this,
    });
  },

  handleResize() {
    const { middle } = this.$refs;
    if (middle) {
      this.isScrollable = middle.scrollHeight > middle.clientHeight;
    }
  },

  closeOpenDrawers() {
    if (forceBackdrop) {
      this.close();
      return;
    }

    openDrawers.forEach((drawer) => {
      drawer.close();
    });
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('drawer', drawer);
});
